import { client } from ".";

export const getCallHistory = async (
  astrologerId,
  accessToken,
  filters = {}
) => {
  if (!astrologerId) {
    return { status: 200, data: { appointments: [] } };
  }
  if (!accessToken) {
    return { status: 200, data: { appointments: [] } };
  }

  try {
    const {
      page,
      limit,
      startDate,
      endDate,
      status,
      mode,
      searchTerm,
      durations,
      sortBy = "newest",
    } = filters;

    const queryParams = new URLSearchParams();

    if (page) queryParams.append("page", page);
    if (limit) queryParams.append("limit", limit);
    if (startDate) queryParams.append("startDate", startDate);
    if (endDate) queryParams.append("endDate", endDate);
    if (status) queryParams.append("status", status.toLowerCase());
    if (mode) queryParams.append("mode", mode);
    if (searchTerm) queryParams.append("searchTerm", searchTerm);
    if (durations) queryParams.append("durations", durations);
    if (sortBy) queryParams.append("sortBy", sortBy);

    const queryString = queryParams.toString();
    const url = `/api/astrologer/call-history/${astrologerId}${
      queryString ? `?${queryString}` : ""
    }`;
    const response = await client.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return {
      status: response.status,
      data: response.data.data || response.data
    };
  } catch (err) {
    console.error("Call history API call failed:", err);
    return {
      status: err.response?.status || 500,
      error: err.message,
      data: { appointments: [] }
    };
  }
};

export const getAppointments = async (
  astrologerId,
  accessToken,
  filters = {}
) => {
  // Return early with a proper response object if astrologerId is missing
  if (!astrologerId) {
    console.log("Astrologer ID is undefined - skipping API call");
    return { status: 200, data: { appointments: [] } };
  }
  
  // Return early if accessToken is missing
  if (!accessToken) {
    console.log("Access token is undefined - skipping API call");
    return { status: 200, data: { appointments: [] } };
  }

  try {
    const {
      page,
      limit,
      date,
      startDate,
      endDate,
      status,
      mode,
      type,
      typeofmeeting,
      searchTerm,
      durations,
      sortBy = "newest",
    } = filters;

    const queryParams = new URLSearchParams();

    if (page) queryParams.append("page", page);
    if (limit) queryParams.append("limit", limit);
    if (date) queryParams.append("date", date);
    if (startDate) queryParams.append("startDate", startDate);
    if (endDate) queryParams.append("endDate", endDate);
    if (status) queryParams.append("status", status.toLowerCase());
    if (mode) queryParams.append("mode", mode);
    if (type) queryParams.append("type", type);
    if (typeofmeeting) queryParams.append("typeofmeeting", typeofmeeting);
    if (searchTerm) queryParams.append("searchTerm", searchTerm);
    if (durations) queryParams.append("durations", durations);
    if (sortBy) queryParams.append("sortBy", sortBy);

    const queryString = queryParams.toString();
    const url = `/api/astrologer/appointments/${astrologerId}${
      queryString ? `?${queryString}` : ""
    }`;    
    const response = await client.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    
    return {
      status: response.status,
      data: response.data.data || response.data
    };
  } catch (err) {
    console.error("API call failed:", err);
    // Return an error object instead of throwing to prevent UI crashes
    return { 
      status: err.response?.status || 500, 
      error: err.message,
      data: { appointments: [] } 
    };
  }
};

/**
 * Fetch a single meeting by ID. Used by the in-call screens to surface
 * the user's name + booking details without doing a wider appointments
 * fetch and guessing which row applies. The endpoint is public on the
 * server side; that's a pre-existing decision we're not touching here
 * — the in-call screen only ever knows the meetingId from its own URL,
 * so it's not a discovery surface.
 *
 * Returns the populated meeting (user + astrologer subdocs) on 200, or
 * null on any error (4xx/5xx/network). Callers should fall back to URL-
 * derived defaults when null is returned rather than crash the call UI.
 */
export const getMeetingById = async (meetingId) => {
  if (!meetingId) return null;
  try {
    const response = await client.get(`/api/meeting/by-id/${meetingId}`);
    return response.data?.data || null;
  } catch (err) {
    console.warn("[getMeetingById] failed:", err?.message);
    return null;
  }
};